import { trackEvent } from "@/lib/analytics";
import { getCurrentUsage } from "@/lib/usageGate";
import { isPremiumApp } from "@shared/tiers";

// Upgrade-funnel events for the shared paywall dialog. Parameters are limited to: tool slug, tier, uses today,
// placement and (clicks only) the option. Never a file name, file content or anything personal.
//
// Once-only: everything is keyed to one "paywall session" (closed -> open). A second open request while the
// dialog is already showing starts nothing new, and each option's click is counted once per session, so a double
// click, a re-render or React strict mode cannot double fire. Events are fired from this store-level code, not
// from component effects.

export type PaywallPlacement = "download" | "run";
export type PaywallOption = "day_pass" | "monthly" | "pricing";

export interface PaywallEventParams {
  tool_id: string;
  tier: "professional" | "premium";
  uses_today?: number;
  placement: PaywallPlacement;
  option?: PaywallOption;
}

let session: { params: Omit<PaywallEventParams, "option">; clicked: Set<PaywallOption> } | null = null;

/** Only a plain tool slug is ever sent. */
function safeSlug(toolId: string | undefined): string {
  return toolId && /^[a-z0-9][a-z0-9-]{0,63}$/.test(toolId) ? toolId : "unknown";
}

/** Called when the paywall goes from closed to open. Returns false (and fires nothing) if a session is already live. */
export function paywallOpened(toolId: string | undefined, moment?: string): boolean {
  if (session) return false;
  const used = getCurrentUsage()?.usageToday;
  const params: Omit<PaywallEventParams, "option"> = {
    tool_id: safeSlug(toolId),
    tier: isPremiumApp(toolId) ? "premium" : "professional",
    ...(typeof used === "number" ? { uses_today: used } : {}),
    placement: moment === "download" || moment === "copy" || moment === "export" ? "download" : "run",
  };
  session = { params, clicked: new Set() };
  trackEvent("limit_hit", { ...params });
  trackEvent("upgrade_cta_view", { ...params });
  return true;
}

/** The tool slug of the paywall that is open now, if any (for the checkout_start event). */
export function currentPaywallToolId(): string | undefined { return session?.params.tool_id === "unknown" ? undefined : session?.params.tool_id; }

export function paywallClosed() { session = null; }

export function paywallClicked(option: PaywallOption): boolean {
  if (!session || session.clicked.has(option)) return false;
  session.clicked.add(option);
  trackEvent("upgrade_cta_click", { ...session.params, option });
  return true;
}

export function __resetPaywallEventsForTests() { session = null; }
